import ChevronDownThin from '@assets/icons/chevron-down-thin.svg';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Text, TouchableOpacity, View } from 'react-native';
import { BORDER_RADIUS_TINY, FONTS } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { getAlertStyle, Level } from './alert';

interface Props {
  level: Level;
  message: string;
  title: string;
}

export const AlertCollapsible: React.FC<Props> = ({
  level,
  message,
  title,
}) => {
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(0);
  const animatedValueRef = useRef(new Animated.Value(0));

  useEffect(() => {
    Animated.timing(animatedValueRef.current, {
      duration: 250,
      easing: Easing.out(Easing.exp),
      toValue: alertIsOpen ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [alertIsOpen]);

  const [styles, theme] = useStyles();
  const { backgroundColor, color } = getAlertStyle(level, theme);

  return (
    <View
      style={[
        styles.base,
        {
          backgroundColor,
        },
      ]}
    >
      <TouchableOpacity
        activeOpacity={1}
        onPress={() => {
          setAlertIsOpen((open) => !open);
        }}
        style={styles.touchable}
      >
        <Text
          style={[
            styles.title,
            {
              color,
            },
          ]}
        >
          {title}
        </Text>
        <Animated.View
          style={[
            styles.chevronButton,
            {
              transform: [
                {
                  rotate: animatedValueRef.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', '-180deg'],
                  }),
                },
              ],
            },
          ]}
        >
          <ChevronDownThin height={16} stroke={color} width={16} />
        </Animated.View>
      </TouchableOpacity>
      <Animated.View
        style={[
          styles.container,
          {
            height: animatedValueRef.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, currentHeight],
            }),
          },
        ]}
      >
        <View
          onLayout={(e) => {
            setCurrentHeight(e.nativeEvent.layout.height);
          }}
          style={styles.content}
        >
          <Text
            style={[
              styles.text,
              {
                color,
              },
            ]}
          >
            {message}
          </Text>
        </View>
      </Animated.View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderRadius: BORDER_RADIUS_TINY,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  chevronButton: {
    padding: 4,
  },
  container: {
    overflow: 'hidden',
  },
  content: {
    left: 0,
    paddingTop: 8,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  text: {
    ...FONTS.primary,
    fontSize: theme.fontSizes.small,
    lineHeight: 22,
  },
  title: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.tertiary,
    fontSize: theme.fontSizes.small,
    lineHeight: 16,
  },
  touchable: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'space-between',
  },
}));
